import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import {
    Crown,
    CheckCircle,
    XCircle,
    Sparkle,
    ArrowRight,
    Lightning,
    Star,
    Envelope,
    ChartLineUp,
    ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

const PRO_FEATURES = [
    { icon: Sparkle, title: "Unlimited AI CV rewrites", copy: "Rewrite for every job description you apply to. Tailor each version to the ATS keyword profile.", free: "1 rewrite total" },
    { icon: ChartLineUp, title: "LinkedIn profile analyzer", copy: "Score your headline, About, Experience — get a paste-ready rewrite from NaukriDaddy AI.", free: "Locked" },
    { icon: Lightning, title: "Priority visibility to recruiters", copy: "Your profile pins to the top of recruiter shortlists for the JDs you apply to.", free: "Standard ordering" },
    { icon: Envelope, title: "Weekly gap-tracker emails", copy: "Get a personalised weekly nudge if your latest CV still has unresolved gaps.", free: "Not included" },
    { icon: Star, title: "Boost profile badge", copy: "A gold Pro badge on your profile — a trust signal recruiters actually notice.", free: "Not included" },
    { icon: ShieldCheck, title: "1-tap human review upsell", copy: "Skip the queue and pay ₹99 for a human-vetted rewrite when you need certainty.", free: "Same price for Free" },
];

const FAQ = [
    { q: "Is this a subscription that auto-renews?", a: "No. Pro is a one-time 30-day pass. You pay ₹499, get all Pro features for 30 days, and it just expires — no surprise charges." },
    { q: "What happens after 30 days?", a: "Your CV rewrites and LinkedIn analyses stay saved to your account. Rewrites revert to the free-tier limit (1 lifetime free). You can re-purchase Pro any time to unlock again." },
    { q: "How do you protect my card?", a: "All payments are processed by Razorpay (PCI-DSS compliant). We never see or store your card details. Every charge is signature-verified server-side." },
    { q: "Refund policy?", a: "Since Pro unlocks digital features immediately (rewrites, analyses), we don't offer partial refunds. Full refund available within 24 hours of purchase if you haven't triggered a rewrite." },
    { q: "Do recruiters really see me first?", a: "Yes — your profile jumps to the top of recruiter shortlists for every job you apply to while Pro is active. Recruiters see a small crown badge to signal serious intent." },
];

export default function ProPage() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const urlCode = (params.get("code") || "").trim().toUpperCase();
    const [status, setStatus] = useState(null);
    const [buying, setBuying] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        if (user?.role === "candidate") {
            api.get("/cv/pro/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
        }
    }, [user]);

    const goCheckout = async () => {
        if (!user) {
            sessionStorage.setItem("post_login_redirect", "/pro");
            navigate("/candidate/signup?redirect=/pro");
            return;
        }
        if (user.role !== "candidate") {
            // Signed in as recruiter/admin — Pro is candidate-only. Silently sign
            // them out and drop them at the candidate login (with a signup fallback
            // link) so they can switch context in one tap.
            try { await api.post("/auth/logout"); } catch { /* ignore */ }
            setUser(false);
            sessionStorage.setItem("post_login_redirect", "/pro");
            navigate("/candidate/login?redirect=/pro");
            return;
        }
        setBuying(true);
        try {
            const rzOk = await loadRazorpay();
            if (!rzOk) {
                toast.error("Couldn't load payment gateway. Please try again.");
                return;
            }
            const { data: order } = await api.post("/cv/pro/checkout", urlCode ? { code: urlCode } : {});
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy Pro",
                description: order.discount_applied
                    ? `30-day Pro pass · ${order.discount_code} · 20% off applied`
                    : "30-day Pro pass — unlimited rewrites + priority visibility",
                prefill: {
                    email: user.email,
                    name: user.name,
                },
                theme: { color: "#1a1a1a" },
                handler: async (rp) => {
                    try {
                        const { data: verify } = await api.post("/cv/pro/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        setStatus({ active: true, pro_until: verify.pro_until, price_inr: 499, duration_days: 30 });
                        toast.success("You're Pro! Enjoy 30 days of unlimited rewrites.");
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || "Payment verification failed");
                    }
                },
                modal: {
                    ondismiss: () => setBuying(false),
                },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally {
            setBuying(false);
        }
    };

    const isPro = !!status?.active;
    const proUntil = status?.pro_until ? new Date(status.pro_until) : null;

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="pro-page">
            <PublicHeader currentPath="/pro" />

            {/* Hero */}
            <section className="relative bg-ink text-white overflow-hidden">
                <div className="absolute -right-20 -top-20 w-96 h-96 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -left-20 bottom-0 w-72 h-72 bg-cobalt/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 sm:pt-20 sm:pb-24">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] text-splash mb-4 inline-flex items-center gap-2">
                        <Crown size={12} weight="fill" /> NaukriDaddy Pro
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-4">
                        Get seen first. <br />
                        <span className="text-splash">Get hired faster.</span>
                    </h1>
                    <p className="text-base sm:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
                        Unlimited AI CV rewrites, LinkedIn analyzer, and top-of-shortlist visibility with
                        recruiters. All for a 30-day pass — no auto-renew, no surprises.
                    </p>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {isPro ? (
                            <div data-testid="pro-active-badge" className="inline-flex items-center gap-3 bg-splash text-ink px-5 py-3">
                                <Crown size={18} weight="fill" />
                                <div>
                                    <p className="font-heading font-black text-sm">You&apos;re Pro</p>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-ink/70">
                                        Active until {proUntil?.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={goCheckout}
                                disabled={buying}
                                data-testid="pro-cta-checkout"
                                className="btn-gradient px-8 py-4 text-sm font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {buying ? "Opening checkout…" : (
                                    <>
                                        <Crown size={16} weight="fill" />
                                        Go Pro — ₹499 for 30 days
                                    </>
                                )}
                            </button>
                        )}
                        <p className="text-xs text-white/50">
                            One-time payment · No auto-renew · Cancel anytime
                        </p>
                    </div>
                </div>
            </section>

            {/* Feature comparison */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                    Free vs Pro
                </p>
                <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight max-w-2xl mb-10">
                    Everything you get with Pro.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PRO_FEATURES.map(({ icon: Icon, title, copy, free }) => (
                        <div key={title} className="bg-white border border-[#E5E5E5] p-5 sm:p-6 relative">
                            <div className="flex items-start gap-4 mb-3">
                                <div className="w-10 h-10 bg-ink text-splash flex items-center justify-center shrink-0">
                                    <Icon size={18} weight="fill" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-heading text-lg font-bold tracking-tight">{title}</p>
                                    <p className="text-sm text-[#525252] leading-relaxed mt-1">{copy}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pt-4 border-t border-[#F5F5F5]">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Free</p>
                                    <p className="text-xs text-[#525252] flex items-center gap-1.5">
                                        <XCircle size={12} weight="fill" className="text-[#A3A3A3]" />
                                        {free}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-splash mb-1">Pro</p>
                                    <p className="text-xs text-ink font-bold flex items-center gap-1.5">
                                        <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                        Unlimited
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing card */}
            {!isPro && (
                <section data-testid="pro-pricing" className="max-w-3xl mx-auto px-4 sm:px-6 pb-10 sm:pb-16">
                    <div className="bg-ink text-white p-8 sm:p-12 relative overflow-hidden">
                        <div className="absolute -top-16 -right-16 w-64 h-64 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                        {urlCode ? (
                            <div data-testid="pro-discount-banner" className="inline-flex items-center gap-2 bg-splash text-ink px-3 py-1 font-heading font-black text-xs uppercase tracking-wider mb-4 relative">
                                <Sparkle size={12} weight="fill" /> Code {urlCode} · 20% off applied
                            </div>
                        ) : (
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3 relative">
                                30-day Pro pass
                            </p>
                        )}
                        <div className="flex items-baseline gap-2 mb-2 relative">
                            {urlCode ? (
                                <>
                                    <p className="font-heading text-6xl sm:text-7xl font-black tracking-tighter">₹399</p>
                                    <p className="text-white/40 text-lg line-through">₹499</p>
                                </>
                            ) : (
                                <p className="font-heading text-6xl sm:text-7xl font-black tracking-tighter">₹499</p>
                            )}
                            <p className="text-white/60 text-sm">/ 30 days</p>
                        </div>
                        <p className="text-white/60 text-sm mb-6 relative">
                            That&apos;s ₹16/day for unlimited rewrites + priority visibility.
                        </p>
                        <ul className="space-y-2 mb-8 relative">
                            {[
                                "Unlimited AI CV rewrites",
                                "LinkedIn profile analyzer + rewrite",
                                "Priority visibility to recruiters",
                                "Weekly gap-tracker emails",
                                "Pro badge on your profile",
                            ].map((f) => (
                                <li key={f} className="flex items-center gap-2 text-sm">
                                    <CheckCircle size={16} weight="fill" className="text-splash shrink-0" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={goCheckout}
                            disabled={buying}
                            data-testid="pro-pricing-cta"
                            className="w-full btn-gradient px-6 py-4 text-sm font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60 relative"
                        >
                            {buying ? "Opening checkout…" : (<><Crown size={16} weight="fill" /> Start my 30-day Pro pass</>)}
                        </button>
                        <p className="text-[11px] text-white/50 mt-3 text-center relative">
                            Secured by Razorpay · No auto-renew · Full refund within 24h if unused
                        </p>
                    </div>
                </section>
            )}

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                    FAQ
                </p>
                <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight mb-8">
                    Common questions.
                </h2>
                <div className="border border-[#E5E5E5] bg-white">
                    {FAQ.map((item, idx) => (
                        <div key={idx} className="border-b border-[#E5E5E5] last:border-b-0">
                            <button
                                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                data-testid={`pro-faq-${idx}`}
                                className="w-full text-left px-5 sm:px-6 py-4 flex items-center justify-between gap-4 hover:bg-splash/5"
                            >
                                <p className="font-heading font-bold text-sm sm:text-base">{item.q}</p>
                                <ArrowRight
                                    size={14}
                                    weight="bold"
                                    className={`shrink-0 transition-transform ${openFaq === idx ? "rotate-90" : ""}`}
                                />
                            </button>
                            {openFaq === idx && (
                                <div className="px-5 sm:px-6 pb-4 text-sm text-[#525252] leading-relaxed">
                                    {item.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer CTA */}
            {!isPro && (
                <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
                    <div className="bg-splash text-ink p-6 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-ink/70 mb-2">
                                Ready when you are
                            </p>
                            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                                One coffee.
                                <br className="hidden sm:inline" /> Thirty days of Pro.
                            </h2>
                        </div>
                        <button
                            onClick={goCheckout}
                            disabled={buying}
                            data-testid="pro-footer-cta"
                            className="bg-ink hover:bg-cobalt text-white px-6 py-3 sm:py-4 text-xs sm:text-sm font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:opacity-60"
                        >
                            Go Pro — ₹499 <ArrowRight size={14} weight="bold" />
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}
